import React, { useCallback, useEffect, useMemo, useState } from "react";
import { BadgeDollarSign, Check, CreditCard, RefreshCw, Scale } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/domain/Bits";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { api, toastError } from "../../lib/api";

type Entry = {
  id: string; user_name: string; patient_name: string; converted_at?: string;
  commission_status: string; approval_status?: string; commissionable: boolean;
  rule_snapshot?: { rate_amount?: number; currency?: string }; notes?: string;
};
type Rule = {
  id: string; user_id: string; user_name: string; rate_amount: number; currency: string;
  requires_completed_months: number; active: boolean; effective_from?: string; version: number;
};
type Center = {
  rules: Rule[]; entries: Entry[]; total: number; page: number; limit: number;
  summary: { pending: number; approved: number; paid: number; disputed: number };
};

const money = (value: number, currency = "CAD") =>
  new Intl.NumberFormat(undefined, { style: "currency", currency }).format(value || 0);

export default function CommissionCenter() {
  const [data, setData] = useState<Center | null>(null);
  const [status, setStatus] = useState("all");
  const [selected, setSelected] = useState<string[]>([]);
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: result } = await api.get<Center>("/tenant/commission-center", {
        params: { status: status === "all" ? "" : status, limit: 100 },
      });
      setData(result);
      setSelected([]);
    } catch (error) { toastError(error); }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const review = async (entry: Entry, approval_status: "approved" | "rejected" | "disputed") => {
    setBusy(true);
    try {
      await api.patch(`/tenant/commission-ledger/${entry.id}`, { approval_status });
      toast.success(approval_status === "approved" ? "Commission approved." : "Commission review saved.");
      await load();
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const pay = async () => {
    if (!reference.trim()) return toast.error("Enter a payout reference.");
    setBusy(true);
    try {
      await api.post("/tenant/commission-payouts", { entry_ids: selected, reference: reference.trim() });
      toast.success("Payout recorded. Historical commission amounts remain locked.");
      setReference("");
      await load();
    } catch (error) { toastError(error); } finally { setBusy(false); }
  };

  const approvedIds = useMemo(() => (data?.entries || [])
    .filter((e) => e.commissionable && e.approval_status === "approved" && e.commission_status !== "paid")
    .map((e) => e.id), [data]);

  return (
    <>
      <PageHeader title="Commission Center" subtitle="Approve earned commissions, resolve disputes, and record auditable payouts" />
      <div className="mx-auto max-w-7xl space-y-5 p-6 md:p-8" data-testid="commission-center">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[
            ["Pending review", data?.summary.pending || 0, BadgeDollarSign],
            ["Approved", data?.summary.approved || 0, Check],
            ["Paid", data?.summary.paid || 0, CreditCard],
            ["Disputed", data?.summary.disputed || 0, Scale],
          ].map(([label, value, Icon]: any) => (
            <div key={label} className="rounded-lg border border-slate-200 bg-white p-4">
              <Icon className="mb-3 h-4 w-4 text-slate-500" />
              <div className="text-xl font-semibold">{money(value)}</div>
              <div className="text-xs text-slate-500">{label}</div>
            </div>
          ))}
        </div>

        <section className="rounded-lg border border-slate-200 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b p-4">
            <div>
              <h2 className="font-semibold">Commission ledger</h2>
              <p className="text-xs text-slate-500">Amounts use the rule snapshot active when the conversion was recorded.</p>
            </div>
            <div className="flex gap-2">
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All records</SelectItem>
                  <SelectItem value="pending">Pending review</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="disputed">Disputed</SelectItem>
                  <SelectItem value="paid">Paid</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={load}><RefreshCw className="mr-2 h-4 w-4" /> Refresh</Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
                <th className="px-4 py-3"></th><th className="px-4 py-3 text-left">Representative</th>
                <th className="px-4 py-3 text-left">Patient</th><th className="px-4 py-3 text-left">Converted</th>
                <th className="px-4 py-3 text-left">Amount</th><th className="px-4 py-3 text-left">Status</th>
                <th className="px-4 py-3 text-right">Review</th>
              </tr></thead>
              <tbody>{(data?.entries || []).map((entry) => {
                const payable = entry.commissionable && entry.approval_status === "approved" && entry.commission_status !== "paid";
                return <tr key={entry.id} className="border-t">
                  <td className="px-4 py-3"><input type="checkbox" disabled={!payable} checked={selected.includes(entry.id)}
                    onChange={(e) => setSelected((ids) => e.target.checked ? [...ids, entry.id] : ids.filter((id) => id !== entry.id))} /></td>
                  <td className="px-4 py-3">{entry.user_name}</td><td className="px-4 py-3">{entry.patient_name}</td>
                  <td className="px-4 py-3">{entry.converted_at ? new Date(entry.converted_at).toLocaleString() : "—"}</td>
                  <td className="px-4 py-3 font-medium">{money(entry.rule_snapshot?.rate_amount || 0, entry.rule_snapshot?.currency || "CAD")}</td>
                  <td className="px-4 py-3">{entry.commission_status === "paid" ? "Paid" : (entry.approval_status || "Pending").replace(/_/g, " ")}</td>
                  <td className="px-4 py-3 text-right space-x-1">
                    {entry.commissionable && entry.commission_status !== "paid" && <>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => review(entry, "approved")}>Approve</Button>
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => review(entry, "disputed")}>Dispute</Button>
                    </>}
                  </td>
                </tr>;
              })}</tbody>
            </table>
            {!data?.entries.length && <div className="p-8 text-center text-sm text-slate-500">No matching commissions.</div>}
          </div>
          <div className="flex flex-wrap items-center gap-2 border-t p-4">
            <Button variant="outline" onClick={() => setSelected(approvedIds)}>Select approved</Button>
            <Input className="max-w-xs" placeholder="Payout reference / transaction ID" value={reference} onChange={(e) => setReference(e.target.value)} />
            <Button disabled={!selected.length || busy} onClick={pay}><CreditCard className="mr-2 h-4 w-4" /> Record payout ({selected.length})</Button>
          </div>
        </section>

        <section className="rounded-lg border border-slate-200 bg-white p-4">
          <h2 className="font-semibold">Rule history</h2>
          <p className="mb-3 text-xs text-slate-500">Rate changes create new effective records; existing ledger snapshots are never recalculated.</p>
          <div className="space-y-2">{(data?.rules || []).map((rule) => (
            <div key={rule.id} className="flex flex-wrap justify-between gap-2 rounded-md bg-slate-50 p-3 text-sm">
              <span>{rule.user_name} · {money(rule.rate_amount, rule.currency)} per converted patient</span>
              <span className="text-slate-500">after {rule.requires_completed_months} completed months · v{rule.version} · {rule.active ? "Active" : "Inactive"}</span>
            </div>
          ))}</div>
        </section>
      </div>
    </>
  );
}
